/**
 * Publish Decision Logic
 *
 * Pure logic for deciding whether an asset update requires a full content
 * republish.
 *
 * **Root behaviors are never a metadata payload.** They live in the document,
 * at `scenes[n].extras.trace` (CONTENT_MODEL_SPEC §3.6, SCENE_ROOT_SPEC §2.1) —
 * one home, so there is no second copy to disagree with. `Asset.metadata`
 * carried them while the wire format had nowhere to put them; the writer stops
 * emitting them there for ANY content, and the server strips the field on write
 * (WP-A4). The column survives for non-behavior client JSON.
 *
 * What remains is the content comparison: `TraceScene.getExportData()` is
 * snapshotted at open and compared at publish, so an unchanged scene skips the
 * blob upload.
 */

import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import type { BehaviorData } from '../scenedata/behaviordata';

// ── Content snapshot ────────────────────────────────────────────────────────

/**
 * Serialize scene export data into a deterministic string for comparison.
 * Uses JSON.stringify which is stable for the same input structure.
 */
export function snapshotExportData(exportData: SceneObjectData[]): string {
    return JSON.stringify(exportData);
}

// ── Publish decision ────────────────────────────────────────────────────────

export interface AssetPublishDecision {
    /** Whether the scene blob (File + Thumbnail) needs to be uploaded. */
    contentChanged: boolean;
    /**
     * Always `undefined`.
     *
     * Kept on the shape for one release so a consumer that spreads this into a
     * PATCH body keeps compiling while it is updated; the value is never
     * populated, because root behaviors belong to the document (spec §3.6).
     *
     * @deprecated Stop reading this; it will be removed.
     */
    metadata: undefined;
}

/**
 * Determine what needs to be published by comparing current scene state
 * against the snapshot taken at open/last-publish time.
 *
 * @param currentExportData - Current scene export data
 * @param openSnapshot      - Serialized export data from open/last-publish time
 * @param _rootBehaviors    - Ignored. Root behaviors are written into the
 *   document, never into the CMS `metadata` payload, for any content
 *   (spec §3.6). The parameter is kept for one release so callers keep
 *   compiling; it has no effect.
 */
export function resolvePublishDecision(
    currentExportData: SceneObjectData[],
    openSnapshot: string | null,
    _rootBehaviors?: BehaviorData[],
): AssetPublishDecision {
    if (!openSnapshot) {
        // No snapshot → can't compare → assume content changed
        return { contentChanged: true, metadata: undefined };
    }

    const currentSnapshot = snapshotExportData(currentExportData);
    const contentChanged = currentSnapshot !== openSnapshot;

    return { contentChanged, metadata: undefined };
}
